import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const Privacy: React.FC = () => {
  useEffect(() => {
    document.title = "Privacy Policy | PixelShift";
  }, []);

  return (
    <div className="min-h-screen bg-gray-900 text-gray-200 pt-24 pb-16 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 text-sm">
          <Link to="/" className="text-brand-primary hover:underline">Home</Link>
          <span className="mx-2 text-gray-600">→</span>
          <span className="text-gray-400">Privacy Policy</span>
        </div>

        <article className="prose prose-invert prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-white mb-4">Privacy Policy</h1>
          <p className="text-gray-400 mb-8">Last Updated: January 22, 2025</p>

          <div className="bg-green-900/20 border-l-4 border-green-500 p-6 rounded-r-lg mb-8">
            <p className="text-white font-semibold mb-2">🔒 TL;DR - Your Privacy is Sacred</p>
            <p className="text-gray-300">
              PixelShift does not collect, store, or transmit any personal data. All image conversion happens in your browser. 
              We don't use cookies, analytics, or tracking on the converter. Your images never leave your device.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-white mt-8 mb-4">1. Information We DON'T Collect</h2>
          <p className="text-gray-300 mb-4">PixelShift is built with privacy as the #1 priority. We do not collect:</p>
          <ul className="space-y-2 mb-6 text-gray-300">
            <li>• Your images or files</li>
            <li>• Your personal information</li>
            <li>• Your browsing history</li>
            <li>• Cookies for tracking (only for authentication if you use Pro features)</li>
            <li>• Your IP address for user profiling</li>
          </ul>

          <h2 className="text-2xl font-bold text-white mt-8 mb-4">2. How Image Conversion Works</h2>
          <p className="text-gray-300 mb-4">
            <strong className="text-white">Web Application:</strong> When you use PixelShift on pixelshifts.app, 
            all image processing happens entirely in your web browser using JavaScript and Canvas API. Your images are:
          </p>
          <ul className="space-y-2 mb-6 text-gray-300">
            <li>• Never uploaded to our servers</li>
            <li>• Never stored anywhere</li>
            <li>• Never transmitted across the internet</li>
            <li>• Processed locally on your device only</li>
          </ul>

          <p className="text-gray-300 mb-4">
            <strong className="text-white">Browser Extension:</strong> When you use the PixelShift Chrome extension, 
            the image URL is passed to our web application to open the converter. The actual image data is then 
            processed client-side as described above.
          </p>

          <h2 className="text-2xl font-bold text-white mt-8 mb-4">3. Analytics & Cookies</h2>
          
          <h3 className="text-xl font-bold text-white mt-6 mb-3">Google Analytics (Website Only)</h3>
          <p className="text-gray-300 mb-4">
            We use Google Analytics on our website to understand:
          </p>
          <ul className="space-y-2 mb-4 text-gray-300">
            <li>• How many people visit our site</li>
            <li>• Which pages are most popular</li>
            <li>• General location (country/city level)</li>
            <li>• Browser and device type</li>
          </ul>
          <p className="text-gray-300 mb-6">
            This data is anonymous and aggregated. We use it solely to improve the website. 
            You can block Google Analytics with browser extensions like uBlock Origin.
          </p>

          <h3 className="text-xl font-bold text-white mt-6 mb-3">Pro Features (Future)</h3>
          <p className="text-gray-300 mb-6">
            If you subscribe to PixelShift Pro (not yet available), we will use authentication cookies to keep you 
            logged in. These are essential cookies only - we still won't track your browsing or conversion activity.
          </p>

          <h2 className="text-2xl font-bold text-white mt-8 mb-4">4. Browser Extension Permissions</h2>
          <p className="text-gray-300 mb-4">Our Chrome extension requests these permissions:</p>

          <div className="bg-gray-800 p-6 rounded-lg mb-6">
            <h4 className="text-lg font-bold text-white mb-2">contextMenus</h4>
            <p className="text-gray-300 mb-4">
              <strong>Why:</strong> To show the "Convert with PixelShift" option when you right-click images.
            </p>

            <h4 className="text-lg font-bold text-white mb-2">&lt;all_urls&gt; (host_permissions)</h4>
            <p className="text-gray-300">
              <strong>Why:</strong> To access images from any website you choose to convert. We only access images 
              when you explicitly right-click and select our extension. No background data collection.
            </p>
          </div>

          <h2 className="text-2xl font-bold text-white mt-8 mb-4">5. Third-Party Services</h2>
          
          <h3 className="text-xl font-bold text-white mt-6 mb-3">Cloudflare Pages (Hosting)</h3>
          <p className="text-gray-300 mb-4">
            Our website is hosted on Cloudflare Pages. Cloudflare may collect:
          </p>
          <ul className="space-y-2 mb-4 text-gray-300">
            <li>• Server logs (IP address, timestamp, page visited)</li>
            <li>• DDoS protection data</li>
          </ul>
          <p className="text-gray-300 mb-6">
            See Cloudflare's privacy policy: https://www.cloudflare.com/privacypolicy/
          </p>

          <h3 className="text-xl font-bold text-white mt-6 mb-3">Formspree (Waitlist)</h3>
          <p className="text-gray-300 mb-6">
            If you join our Pro waitlist, your email is stored by Formspree (our form service). We only use this 
            to notify you when Pro features launch. You can unsubscribe anytime.
          </p>

          <h3 className="text-xl font-bold text-white mt-6 mb-3">Stripe (Future Payments)</h3>
          <p className="text-gray-300 mb-6">
            When we launch Pro features, payments will be processed by Stripe. We don't store your credit card 
            information - Stripe handles all payment data securely.
          </p>

          <h2 className="text-2xl font-bold text-white mt-8 mb-4">6. Data Retention</h2>
          <p className="text-gray-300 mb-6">
            Since we don't collect your data, there's nothing to retain! Your images are processed in real-time 
            in your browser and immediately discarded.
          </p>

          <h2 className="text-2xl font-bold text-white mt-8 mb-4">7. Children's Privacy</h2>
          <p className="text-gray-300 mb-6">
            PixelShift is safe for all ages. We don't knowingly collect information from anyone, including children 
            under 13. Since we don't collect data at all, COPPA compliance is inherent.
          </p>

          <h2 className="text-2xl font-bold text-white mt-8 mb-4">8. GDPR & CCPA Compliance</h2>
          
          <h3 className="text-xl font-bold text-white mt-6 mb-3">EU Users (GDPR)</h3>
          <p className="text-gray-300 mb-4">Under GDPR, you have rights to:</p>
          <ul className="space-y-2 mb-6 text-gray-300">
            <li>• Access your data (we have none)</li>
            <li>• Delete your data (we have none)</li>
            <li>• Port your data (we have none)</li>
          </ul>
          <p className="text-gray-300 mb-6">
            Since we don't collect personal data, these rights are automatically fulfilled.
          </p>

          <h3 className="text-xl font-bold text-white mt-6 mb-3">California Users (CCPA)</h3>
          <p className="text-gray-300 mb-6">
            We do not sell your personal information. We don't collect it in the first place.
          </p>

          <h2 className="text-2xl font-bold text-white mt-8 mb-4">9. Security</h2>
          <p className="text-gray-300 mb-6">
            The best security is not storing your data at all! Since all processing happens client-side:
          </p>
          <ul className="space-y-2 mb-6 text-gray-300">
            <li>• No risk of data breaches (we have no data to breach)</li>
            <li>• No risk of unauthorized access</li>
            <li>• No risk of data leaks</li>
          </ul>
          <p className="text-gray-300 mb-6">
            Your images stay on your device, encrypted by your browser's security.
          </p>

          <h2 className="text-2xl font-bold text-white mt-8 mb-4">10. Changes to This Policy</h2>
          <p className="text-gray-300 mb-6">
            If we make material changes to this privacy policy, we'll update the "Last Updated" date at the top 
            and notify users via our website. Continued use of PixelShift after changes constitutes acceptance.
          </p>

          <h2 className="text-2xl font-bold text-white mt-8 mb-4">11. Contact Us</h2>
          <p className="text-gray-300 mb-4">
            Questions about privacy? We're happy to help:
          </p>
          <ul className="space-y-2 mb-6 text-gray-300">
            <li>• Email: <a href="mailto:hi@pixelshifts.app" className="text-brand-primary hover:underline">hi@pixelshifts.app</a></li>
            <li>• Website: <Link to="/" className="text-brand-primary hover:underline">pixelshifts.app</Link></li>
            <li>• Blog: <Link to="/blog" className="text-brand-primary hover:underline">pixelshifts.app/blog</Link></li>
          </ul>

          <div className="bg-gray-800 border-l-4 border-brand-primary p-6 rounded-r-lg mt-8">
            <p className="text-white font-semibold mb-2">📜 Summary</p>
            <p className="text-gray-300">
              PixelShift is designed to be the most private image converter available. We achieve this by doing 
              all processing in your browser, never uploading your images, and collecting minimal analytics. 
              Your privacy isn't just a feature - it's our core principle.
            </p>
          </div>
        </article>

        <div className="mt-12 text-center bg-gray-800 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-white mb-4">Ready to Convert Your Images?</h3>
          <Link to="/" className="inline-block bg-brand-primary text-white font-bold py-3 px-8 rounded-lg hover:bg-brand-secondary transition-all duration-200">
            Try PixelShift Now - Free & Private
          </Link>
        </div>

        <div className="mt-8 text-center">
          <Link to="/" className="text-brand-primary hover:underline">← Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default Privacy;




